import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const useCompetitionRequests = () => {
  const [activeTab, setActiveTab] = useState('pending');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0
  });
  const [filters, setFilters] = useState({
    status: 'pending',
    type: 'all', // all, sent, received
    requestType: 'all'
  });

  const fetchRequests = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const endpoint = activeTab === 'pending' ? '/competition-requests' : '/competition-requests/ended';
      const params = {
        page,
        limit: 20,
        status: activeTab === 'pending' ? 'pending' : filters.status,
        type: filters.type,
        ...(filters.requestType !== 'all' && { requestType: filters.requestType })
      };

      const response = await api.get(endpoint, { params });
      const data = response.data.data;

      setRequests(data.requests || []);
      setPagination(data.pagination || { currentPage: 1, totalPages: 1, totalItems: 0 });
      setError(null);
    } catch (err) {
      console.error('Error fetching competition requests:', err);
      setError('Failed to load competition requests');
    } finally {
      setLoading(false);
    }
  }, [activeTab, filters]);

  useEffect(() => {
    fetchRequests();
  }, [activeTab, filters, fetchRequests]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setFilters(prev => ({ ...prev, status: tab === 'pending' ? 'pending' : 'all' }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handlePageChange = (page) => {
    fetchRequests(page);
  };

  const handleCancelRequest = async (requestId) => {
    try {
      await api.delete(`/competition-requests/${requestId}`, {
        data: {} // Send empty body to ensure req.body is not undefined
      });
      fetchRequests(pagination.currentPage);
      return { success: true, message: 'Request cancelled successfully' };
    } catch (err) {
      console.error('Error cancelling request:', err);
      return { success: false, message: 'Failed to cancel request' };
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      await api.post('/competition-requests/accept', { requestId });
      fetchRequests(pagination.currentPage);
      return { success: true, message: 'Request accepted successfully' };
    } catch (err) {
      console.error('Error accepting request:', err);
      return { success: false, message: 'Failed to accept request' };
    }
  };

  const handleRejectRequest = async (requestId, reason) => {
    try {
      await api.post('/competition-requests/reject', { requestId, reason });
      fetchRequests(pagination.currentPage);
      return { success: true, message: 'Request rejected successfully' };
    } catch (err) {
      console.error('Error rejecting request:', err);
      return { success: false, message: 'Failed to reject request' };
    }
  };

  return {
    // State
    activeTab,
    requests,
    loading,
    error,
    pagination,
    filters,
    
    // Actions
    handleTabChange,
    handleFilterChange,
    handlePageChange,
    handleCancelRequest,
    handleAcceptRequest,
    handleRejectRequest,
    
    // Utils
    fetchRequests
  };
};

export default useCompetitionRequests; 